import { jsx as _jsx } from "react/jsx-runtime";
import { forwardRef, useImperativeHandle } from 'react';
import { FiberProvider, useFiber } from 'its-fine';
import { RunnerBridge } from '../../../helpers';
const REMOTE_IMAGE_COMPONENTS = new Set([
    'RCTImageView',
    'ReactImageView',
    'FFFastImageView',
    'FastImageView',
    'ExpoImageView',
    'EXImageView',
    'RNSVGImage',
    'RNSVGSvgView',
    'SvgView',
]);
function isRemoteImageSource(source) {
    if (!source)
        return false;
    if (typeof source === 'string') {
        return /^https?:\/\//.test(source);
    }
    if (typeof source === 'object') {
        if (Array.isArray(source)) {
            return source.some(isRemoteImageSource);
        }
        if (typeof source.uri === 'string' && /^https?:\/\//.test(source.uri)) {
            return true;
        }
    }
    return false;
}
function isRemoteImageComponent(fiber) {
    const { type, pendingProps, memoizedProps } = fiber;
    const componentName = typeof type === 'string' ? type : (type === null || type === void 0 ? void 0 : type.displayName) || (type === null || type === void 0 ? void 0 : type.name);
    if (!componentName || !REMOTE_IMAGE_COMPONENTS.has(componentName))
        return false;
    const props = pendingProps || memoizedProps;
    if (!props)
        return false;
    const source = props.source || props.src || props.srcSet || props.svgXmlData;
    return isRemoteImageSource(source);
}
const MetadataCollector = forwardRef(({ children }, ref) => {
    const fiber = useFiber();
    useImperativeHandle(ref, () => ({
        collectMetadata: () => {
            var _a;
            if (!fiber) {
                RunnerBridge.log('No fiber node available.');
                return {
                    viewProps: {},
                    texts: [],
                    rendersRemoteImages: false,
                };
            }
            const metadata = {
                viewProps: {},
                texts: [],
                rendersRemoteImages: false,
            };
            const visited = new Set();
            const queue = [fiber, fiber.alternate];
            while (queue.length > 0) {
                const currentFiber = queue.shift();
                if (!currentFiber || visited.has(currentFiber))
                    continue;
                visited.add(currentFiber);
                const { pendingProps, stateNode, memoizedProps, type } = currentFiber;
                // In new architecture, the native tag is on the canonical fiber
                const nativeTag = (stateNode === null || stateNode === void 0 ? void 0 : stateNode._nativeTag) || ((_a = stateNode === null || stateNode === void 0 ? void 0 : stateNode.canonical) === null || _a === void 0 ? void 0 : _a.nativeTag);
                // Collect view props with native tags
                if (nativeTag) {
                    metadata.viewProps[nativeTag] = {
                        style: pendingProps.style,
                        testID: pendingProps.testID,
                        className: type || undefined,
                    };
                }
                // Extract text from props
                extractTextFromProps(pendingProps, metadata.texts);
                extractTextFromProps(memoizedProps, metadata.texts);
                if (!metadata.rendersRemoteImages && isRemoteImageComponent(currentFiber)) {
                    metadata.rendersRemoteImages = true;
                }
                if (currentFiber.child)
                    queue.push(currentFiber.child);
                if (currentFiber.sibling)
                    queue.push(currentFiber.sibling);
            }
            // Remove duplicates
            metadata.texts = [...new Set(metadata.texts)];
            return metadata;
        },
    }));
    // Simplified helper that focuses on children
    function extractTextFromProps(props, texts) {
        if (!props)
            return;
        // Direct string
        if (typeof props === 'string') {
            texts.push(props);
            return;
        }
        // Process object properties - focus on children
        if (typeof props === 'object') {
            // Check children
            if (props.children) {
                if (typeof props.children === 'string') {
                    texts.push(props.children);
                }
                else if (Array.isArray(props.children)) {
                    props.children.forEach((child) => {
                        if (typeof child === 'string') {
                            texts.push(child);
                        }
                    });
                }
            }
        }
    }
    return children;
});
export const MetadataProvider = forwardRef(({ children }, ref) => {
    return (_jsx(FiberProvider, { children: _jsx(MetadataCollector, { ref: ref, children: children }) }));
});
//# sourceMappingURL=MetadataProvider.js.map